import React, { useState, useEffect } from 'react';
import { 
  Receipt, CheckCircle2, XCircle, Clock, Eye,
  Plane, Heart, Monitor, GraduationCap, Utensils, FileText,
  Search, Filter, DollarSign, AlertCircle, ArrowUpRight, Send, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus, getReimbursementCategoryInfo } from '../../../lib/payrollCalculations';
import { formatDate, formatTimeAgo } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

const categoryIcons = {
  travel: Plane,
  medical: Heart,
  equipment: Monitor,
  training: GraduationCap,
  meals: Utensils,
  other: FileText
};

export default function ApproveReimbursements() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [reimbursements, setReimbursements] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      // Get team members
      const allUsers = await db.users.toArray();
      const team = allUsers.filter(u => 
        u.profile?.department === user.profile?.department && 
        u.id !== user.id &&
        u.role === 'employee'
      );
      setTeamMembers(team);

      // Get reimbursements for team
      const teamIds = team.map(t => t.id);
      const allReimbursements = await db.reimbursements.toArray();
      const teamReimbursements = allReimbursements.filter(r => teamIds.includes(r.userId));
      setReimbursements(teamReimbursements.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReimbursements = reimbursements.filter(r => {
    const employee = teamMembers.find(m => m.id === r.userId);
    const matchesSearch = 
      employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase());
    let matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    // Special filter for sent-back items
    if (statusFilter === 'sent_back') {
      matchesStatus = r.status === 'pending' && r.sentBackBy;
    } else if (statusFilter === 'pending') {
      matchesStatus = r.status === 'pending';
    }
    return matchesSearch && matchesStatus;
  });

  const pendingCount = reimbursements.filter(r => r.status === 'pending').length;
  const sentBackCount = reimbursements.filter(r => r.status === 'pending' && r.sentBackBy).length;
  const pendingAmount = reimbursements.filter(r => r.status === 'pending').reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const approvedAmount = reimbursements.filter(r => r.status === 'approved').reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const getEmployeeName = (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    return member ? `${member.profile?.firstName} ${member.profile?.lastName}` : 'Unknown';
  };

  const getEmployeeDetails = (userId) => {
    return teamMembers.find(m => m.id === userId);
  };

  const handleApprove = async (id) => {
    try {
      await db.reimbursements.update(id, {
        status: 'approved',
        approvedBy: user.id,
        approvedAt: new Date().toISOString()
      });
      toast.success('Reimbursement approved');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to approve reimbursement');
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await db.reimbursements.update(id, {
        status: 'rejected',
        rejectionReason,
        rejectedBy: user.id,
        rejectedAt: new Date().toISOString()
      });
      toast.success('Reimbursement rejected');
      setShowDetailModal(false);
      setRejectionReason('');
      loadData();
    } catch (error) {
      toast.error('Failed to reject reimbursement');
    }
  };

  const handleForwardToCEO = async (id) => {
    try {
      await db.reimbursements.update(id, {
        status: 'pending_ceo',
        forwardedBy: user.id,
        forwardedAt: new Date().toISOString(),
        forwardNote: `Forwarded by ${user.profile?.firstName} ${user.profile?.lastName} for CEO review`
      });
      toast.success('Reimbursement forwarded to CEO for review');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to forward reimbursement');
    }
  };

  const viewDetails = (reimbursement) => {
    setSelectedReimbursement(reimbursement);
    setRejectionReason('');
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Reimbursements</h1>
          <p className="text-gray-500 mt-1">Review and approve expense claims from your team</p>
        </div>
        <div className="flex gap-2">
          {sentBackCount > 0 && (
            <Badge className="bg-orange-100 text-orange-700 px-3 py-1 text-sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              {sentBackCount} Returned by CEO
            </Badge>
          )}
          {pendingCount > 0 && (
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1 text-sm">
              <AlertCircle className="w-4 h-4 mr-2" />
              {pendingCount} Pending Approval
            </Badge>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Claims</p>
                <p className="text-2xl font-bold text-gray-900">{reimbursements.length}</p>
              </div>
              <Receipt className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(pendingAmount)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Approved</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(approvedAmount)}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by employee or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="sent_back">Sent Back by CEO</SelectItem>
                <SelectItem value="pending_ceo">Pending CEO Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reimbursements List */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReimbursements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No reimbursements found
                  </TableCell>
                </TableRow>
              ) : (
                filteredReimbursements.map(reimbursement => {
                  const categoryInfo = getReimbursementCategoryInfo(reimbursement.category);
                  const Icon = categoryIcons[reimbursement.category] || FileText;
                  const isSentBack = reimbursement.status === 'pending' && reimbursement.sentBackBy;
                  
                  return (
                    <TableRow 
                      key={reimbursement.id} 
                      className={`hover:bg-gray-50 ${isSentBack ? 'bg-orange-50/50 border-l-4 border-l-orange-400' : reimbursement.status === 'pending' ? 'bg-amber-50/30' : ''}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                            {getEmployeeName(reimbursement.userId).split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="font-medium">{getEmployeeName(reimbursement.userId)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={categoryInfo.color}>
                          <Icon className="w-3 h-3 mr-1" />
                          {categoryInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs text-gray-600">
                        <div>
                          <p className="truncate">{reimbursement.description || '-'}</p>
                          {isSentBack && reimbursement.ceoComment && (
                            <div className="mt-1 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                              <span className="font-medium text-orange-700">CEO Comment: </span>
                              <span className="text-orange-600">{reimbursement.ceoComment}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(reimbursement.amount)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={getPayrollStatusColor(reimbursement.status)}>
                            {formatPayrollStatus(reimbursement.status)}
                          </Badge>
                          {isSentBack && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">
                              <RotateCcw className="w-3 h-3 mr-1" />
                              CEO Returned
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {formatTimeAgo(reimbursement.submittedAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewDetails(reimbursement)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {reimbursement.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleForwardToCEO(reimbursement.id)}
                                title="Forward to CEO"
                              >
                                <ArrowUpRight className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleApprove(reimbursement.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => viewDetails(reimbursement)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {reimbursement.status === 'pending_ceo' && (
                            <Badge className="bg-blue-100 text-blue-700">
                              <Send className="w-3 h-3 mr-1" />
                              With CEO
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg" style={{height: '95vh', maxHeight: '95vh', overflowY: 'auto'}}>
          <DialogHeader>
            <DialogTitle>Reimbursement Details</DialogTitle>
            <DialogDescription>
              Review the expense claim details
            </DialogDescription>
          </DialogHeader>

          {selectedReimbursement && (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                  {getEmployeeName(selectedReimbursement.userId).split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{getEmployeeName(selectedReimbursement.userId)}</p>
                  <p className="text-sm text-gray-500">
                    {getEmployeeDetails(selectedReimbursement.userId)?.profile?.position}
                  </p>
                </div>
              </div>

              {/* Claim Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <Badge className={getReimbursementCategoryInfo(selectedReimbursement.category).color}>
                    {getReimbursementCategoryInfo(selectedReimbursement.category).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedReimbursement.amount)}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-900">{selectedReimbursement.description || 'No description provided'}</p>
              </div>

              {selectedReimbursement.receiptNumber && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Receipt Number</p>
                  <p className="text-gray-900">#{selectedReimbursement.receiptNumber}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="font-medium">{formatDate(selectedReimbursement.submittedAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge className={getPayrollStatusColor(selectedReimbursement.status)}>
                    {formatPayrollStatus(selectedReimbursement.status)}
                  </Badge>
                </div>
              </div>

              {selectedReimbursement.status === 'rejected' && selectedReimbursement.rejectionReason && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                  <p className="text-sm text-red-600">{selectedReimbursement.rejectionReason}</p>
                </div>
              )}

              {selectedReimbursement.status === 'pending_ceo' && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-700">Forwarded to CEO for Review</p>
                  <p className="text-sm text-blue-600">{selectedReimbursement.forwardNote || 'Awaiting CEO decision'}</p>
                </div>
              )}

              {selectedReimbursement.status === 'pending' && selectedReimbursement.sentBackBy && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-1">
                    <RotateCcw className="w-4 h-4 text-orange-600" />
                    <p className="text-sm font-medium text-orange-700">Returned by CEO</p>
                  </div>
                  <p className="text-sm text-orange-600">
                    {selectedReimbursement.ceoComment || 'No comment provided'}
                  </p>
                  {selectedReimbursement.sentBackAt && (
                    <p className="text-xs text-orange-500 mt-1">
                      Returned on {formatDate(selectedReimbursement.sentBackAt)}
                    </p>
                  )}
                </div>
              )}

              {selectedReimbursement.status === 'pending' && (
                <div>
                  <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
                  <Textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
            {selectedReimbursement?.status === 'pending' && (
              <>
                <Button 
                  variant="outline"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => handleForwardToCEO(selectedReimbursement.id)}
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" /> Forward to CEO
                </Button>
                <Button 
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleReject(selectedReimbursement.id)}
                >
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button 
                  onClick={() => handleApprove(selectedReimbursement.id)}
                  className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
